import React, {useState, useEffect, useRef, useMemo} from "react"
import {toast} from "react-toastify"
import useAuth from "auth/useAuth"
import useAreas from "hooks/useAreas"
import {akuzativ} from "utils/utils"
import "./Section.05.Location.css"

const INITIAL_ZOOM = 7
const INITIAL_POSITION = { lat: 44.2, lng: 20.6 }
const MAP_ID = 'gss-rescue-map' // Create this Map ID in Google Cloud Console

export default function LocationSection(params) {

    const {rescue} = params
    const {auth, API} = useAuth()
    const {areas} = useAreas()
    const mapRef = useRef()
    const [overlay, setOverlay] = useState([])
    const [zoom, setZoom] = useState(() => {
        const savedZoom = rescue.rescue.location?.zoom ?? INITIAL_ZOOM
        return savedZoom < 14 ? 15 : savedZoom
    })
    const [area, setArea] = useState()
    const [locationArea, setLocationArea] = useState()
    const [lat, setLat] = useState()
    const [lon, setLon] = useState()
    const [alt, setAlt] = useState()
    const [acc, setAcc] = useState()
    const [pistes, setPistes] = useState([])
    const [piste, setPiste] = useState()
    const [offPiste, setOffPiste] = useState(false)
    const [description, setDescription] = useState("")
    const [marker, setMarker] = useState()
    const [locationSet, setLocationSet] = useState(false)
    const [initialized, setInitialized] = useState(false)
    const areaKnown = useRef(false)
    const [mapsLoaded, setMapsLoaded] = useState(typeof window.google?.maps?.Map === 'function')

    useEffect(() => {
        if (mapsLoaded) return
        const interval = setInterval(() => {
            if (typeof window.google?.maps?.Map === 'function') {
                setMapsLoaded(true)
                clearInterval(interval)
            }
        }, 100)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        if (!rescue.ready || initialized) return
        const location = rescue.rescue.location
        if (location) {
            // If saved zoom is too low, use 15 instead
            const savedZoom = location.zoom ?? INITIAL_ZOOM
            const initialZoom = savedZoom < 14 ? 15 : savedZoom
            setZoom(initialZoom)
            setArea(rescue.rescue.area ?? location.area)
            setLocationArea(location.area)
            setLat(location.lat)
            setLon(location.lon)
            setAlt(location.alt)
            setAcc(location.acc)
            setPiste(location.piste)
            setOffPiste(location.off_piste)
            setDescription(location.description ?? '')
            setLocationSet(true)
        }
        else if (!locationSet) {
            getCurrentPosition()
        }
    }, [rescue.ready])

    // Sync local area state when rescue.area changes externally (e.g., from AreaReport)
    useEffect(() => {
        if (!rescue.ready || !initialized) return
        if (rescue.rescue.area !== area) {
            setArea(rescue.rescue.area)
        }
    }, [rescue.rescue.area])

    useEffect(() => {
        if (!rescue.ready) return
        if (!locationSet) return
        rescue.setFields({
            area,
            location: {lat, lon, zoom, area: locationArea, alt, acc, piste, off_piste: offPiste, description}
        })
    }, [lat, lon, piste, area, locationArea, description, offPiste, zoom])

    
    const onMapIdle = () => {
    }

    const onMapClick = (event) => {
        if (!marker) {
      		setLat(event.latLng.lat())
      		setLon(event.latLng.lng())
      		setLocationSet(true)
        }
    }


    const showOverlay = (map) => {
        if (!window.google) return
        let olay = [...overlay]
		if (olay.length === 0) {			
			var osmMapTypeOptions1 = {
				getTileUrl: function(coord, zoom) { return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/${zoom}/${coord.y}/${coord.x}`; },
				tileSize: new window.google.maps.Size(256, 256),
				isPng: true,
				maxZoom: 18,
				minZoom: 3,
				name: "ArcGIS"
			}
			
			var osmMapTypeOptions2 = {
				getTileUrl: function(coord, zoom) { return `https://tiles.opensnowmap.org/pistes/${zoom}/${coord.x}/${coord.y}.png`; },
				tileSize: new window.google.maps.Size(256, 256),
				isPng: true,
				maxZoom: 18,
				minZoom: 3,
				name: "OpenSnowMap"
			}

			olay = []
			olay.push(new window.google.maps.ImageMapType(osmMapTypeOptions1))	
			olay.push(new window.google.maps.ImageMapType(osmMapTypeOptions2))
			setOverlay(olay)	
		}
		
		if (map.overlayMapTypes.length === 0) {
    		for (const ol of olay)
    			map.overlayMapTypes.push(ol)
        }
	}
    

    const map = useMemo(() => {
        if (!mapRef.current || !rescue.ready || !mapsLoaded) return null

    	const zoom = rescue.rescue.location?.zoom ?? INITIAL_ZOOM
        const lat = rescue.rescue.location?.lat ?? INITIAL_POSITION.lat
        const lon = rescue.rescue.location?.lon ?? INITIAL_POSITION.lng

  		const mapOptions = {
  			zoom: zoom,
  			center: {lat: lat, lng: lon},
  			streetViewControl: false,
  			scaleControl: true,
  			mapTypeControl: false,
  			mapTypeId: 'terrain',
  			mapId: MAP_ID
  		}

  		const gmap = new window.google.maps.Map(mapRef.current, mapOptions)
  		gmap.addListener('click', onMapClick)
  		gmap.addListener('idle', onMapIdle)
  		gmap.addListener('zoom_changed', () => {
  			setZoom(gmap.getZoom())
  		})
  		showOverlay(gmap)
  		return gmap
    }, [mapRef.current, rescue.ready, mapsLoaded])

    // Sync zoom state to map
    useEffect(() => {
        if (!map || zoom === undefined) return
        if (map.getZoom() !== zoom) {
            map.setZoom(zoom)
        }
    }, [zoom, map])


    const getLocations = () => {
        if (!lat || !lon) return
    	auth(API.locationsGet, {lat, lon}).then(response => {
            const data = response.data
            const newArea = data.area ?? '-'
            // Only auto-update area if user hasn't manually changed it
            if (area === locationArea) {
                if (areaKnown.current && area && area !== newArea) {
                    if (newArea === '-') toast.info('Povredna lista više ne ulazi u dnevni izveštaj')
                    else toast.info(`Povredna lista sada ulazi u dnevni izveštaj za ${akuzativ(newArea)}`)
                }
                setArea(newArea)
            }
            setLocationArea(newArea)
            areaKnown.current = true
            setPistes(data.pistes ?? [])
            if (initialized) {
                setPiste(data.pistes?.[0]?.piste ?? null)
            }
            setInitialized(true)
        }).catch(response => {
            console.log("getLocations catch", response)
        })
	}
    
   	
    const zoomTo = () => {
        if (!map) return
  		var currentZoom = map.getZoom()
  		var newZoom
  		if (currentZoom < 14) newZoom = 15
  		else if (currentZoom < 18) newZoom = currentZoom + 1
  		else return

  		map.setZoom(newZoom)
  		setZoom(newZoom)
  	}
  	
  	const onMarkerDragRef = useRef()
  	onMarkerDragRef.current = (m) => {
  		const pos = m.position
  		// position can be LatLng (methods) or LatLngLiteral (properties)
  		const newLat = typeof pos.lat === 'function' ? pos.lat() : pos.lat
  		const newLon = typeof pos.lng === 'function' ? pos.lng() : pos.lng
  		setLat(newLat)
  		setLon(newLon)
  		setPiste(null)
  		setAlt(null)
  		setAcc(null)
  	}

  	useEffect(() => {
      	if (!map || !window.google) return
      	window.google.maps.event.clearListeners(map, 'click')
      	map.addListener('click', onMapClick)
  	}, [marker])



  	useEffect(() => {

      	const updateMarker = () => {
      		if (!map || !window.google) return

      		if (!lat || !lon) {
          		if (!!marker) {
              		marker.map = null
              		setMarker(null)
                }
                return
            }

      		if (!marker) {
      			const position = {lat, lng: lon}
      			const pin = new window.google.maps.marker.PinElement({
      				glyph: '',
      				scale: 1.2
      			})
      			const newMarker = new window.google.maps.marker.AdvancedMarkerElement({
      				position: position,
      				map: map,
      				gmpDraggable: true,
      				content: pin.element
      			})
      			newMarker.addListener('dragend', () => onMarkerDragRef.current(newMarker))
      			setMarker(newMarker)
      			map.panTo(position)
      		}
      		else {
      			marker.position = {lat, lng: lon}
      			map.panTo({lat, lng: lon})
      		}
      	}


      	getLocations()
      	updateMarker()
    }, [lat, lon])
       	
       	
   	const receivePosition = (pos) => {
       	setLat(pos.coords.latitude)
  		setLon(pos.coords.longitude)
  		setAlt(pos.coords.altitude)
  		setAcc(pos.coords.accuracy)
  		setPiste(null)
  		setOffPiste(false)
  		setPistes([])
  		setArea('-')
  		setLocationArea('-')
  		setLocationSet(true)
  		setInitialized(true)
  		zoomTo()
   	}

   	const forceCurrentPosition = () => {
       	const toaster = toast.loading("Tražim lokaciju...")
       	setInitialized(false)
       	const onSuccess = (pos) => {
       	    toast.update(toaster, {render: "Lokacija pronađena", type: "success", isLoading: false, autoClose: 1500})
       	    receivePosition(pos)
       	}
       	const onError = () => {
       	    toast.update(toaster, {render: "Nije moguće odrediti lokaciju", type: "error", isLoading: false, autoClose: 2500})
       	}
       	navigator.geolocation?.getCurrentPosition(onSuccess, onError)
   	}
    
    const getCurrentPosition = () => {
		if (!navigator.geolocation) return
    	navigator.geolocation.getCurrentPosition(
        	receivePosition, 
        	// getLocations,
    		// {enableHighAccuracy: true}
    	)
	}
	
	const clearPosition = () => {
		setLat(null)
		setLon(null)
		setAlt(null)
		setAcc(null)
		setPiste(null)
		setOffPiste(false)
		setPistes([])
		setArea('-')
		setLocationArea('-')
	}
	
	const selectPiste = (event) => {
    	const pisteId = event.target.value
    	for (const piste of pistes)
        	if (piste.piste.id.toString() === pisteId.toString()) {
            	setPiste(piste.piste)
            	return
            }
        setPiste(null)
	}
    
    return <li className="accordion" id="accordion-location">
        <input type="checkbox" className="accordion" name="accordion" id="acc-location"/>
        <label className="accordion" htmlFor="acc-location">Lokacija</label>
        <section className="accordion">
            <div className="subsection">                
                {lat && lon && <div>
                    Koordinate: {lat.toFixed(5)}, {lon.toFixed(5)}, {zoom}
                </div>}
                
               	{(area !== '-' || pistes?.length > 0) && <div className="field border">
                   	<label className="above" htmlFor="piste">Staza</label>
               		<select value={piste?.id ?? ""} onChange={selectPiste} className="border" disabled={rescue.isReadOnly}>
                   		<option value="">Van skijališta</option>
                   		{pistes?.map(piste => piste.piste).map(piste => <option key={piste.id} value={piste.id}>
                       		{piste.ref && <>{piste.ref} &bull; </>}{piste.name}
                       	</option>)}
               		</select>
               		<div id="off-piste-row">
                   		<input id="off-piste" type="checkbox" checked={offPiste || false} onChange={e => setOffPiste(off => !off)} disabled={rescue.isReadOnly}/>
                   		<label htmlFor="off-piste">Van staze</label>
                   	</div>
           		</div>}
           		
           		<div className="field border">
           			<label className="above" htmlFor="location_desc">Opis lokacije</label>
          			<input type="text" value={description || ""} onChange={e => setDescription(e.target.value)} placeholder="npr. 20 metara ispod strmog dela" readOnly={rescue.isReadOnly}/>
              	</div>
              	{!rescue.isReadOnly && <div>
              	    <button className="btn btn-light btn-sm" onClick={forceCurrentPosition}><i className="fas fa-map-marker-alt"/>&nbsp;&nbsp;Trenutna lokacija</button>
          		    <button className="btn btn-light btn-sm" onClick={clearPosition}><i className="fas fa-times"/>&nbsp;&nbsp;Obriši lokaciju</button>
              	</div>}
            </div>
            <div id="map" ref={mapRef}/>		  
        </section>
    </li>
    
}
